import Vue from 'vue';
import _ from '../../utils';

import coreTranslationMaps from '../../localization/index';

type CoreTranslationMap = typeof coreTranslationMaps.de;
const coreLanguages: Record<string, CoreTranslationMap> = {
  ...coreTranslationMaps,
};

export interface TranslationMap extends CoreTranslationMap {
  // To be declared by the App
}

export default class Translator {
  readonly translations: Record<string, TranslationMap> = {};
  private readonly current: {languageCode: string, map: TranslationMap} = Vue.observable({}) as any;
  readonly storageKey: string = 'language-frontend';
  private appLanguageCodes: string[];
  private coreLanguageCodes: string[];

  constructor(translations?: Record<string, TranslationMap>) {
    this.appLanguageCodes = Vue.observable([]) as any;
    this.coreLanguageCodes = Vue.observable([]) as any;
    const storedLanguageCode = Vue.$localStorage.get<string>(this.storageKey);
    let languageCode: string | undefined;
    if (storedLanguageCode) {
      languageCode = storedLanguageCode;
    } else if (!languageCode) {
      // No selected language so we use browser language if it's available as a translation map
      languageCode = this.availableBrowserLanguage;
    }
    // Don't call the set method because we don't
    // want to save it to storage until the user explicitly selects a language
    Vue.set(this.current, 'languageCode', languageCode);
    this.loadTranslationMaps(translations);
  }

  get availableBrowserLanguage() {
    const languageCode = this.allBrowserLanguages.find((userLang) => {
      return this.availableAppLanguages.includes(userLang.toLowerCase());
    });
    return languageCode?.toLowerCase();
  }

  get allBrowserLanguages() {
    // Different browsers different properties -.-
    const browserLanguage = navigator.language ?? (navigator as any).userLanguage as string ?? (navigator as any).browserLanguage as string;
    // Fallback to singular language in case languages is not available
    const browserLanguages = navigator.languages ?? [browserLanguage];
    // Get rid of any reference
    const userLangs = [...browserLanguages];
    userLangs.forEach((lang) => {
      const splitLang = lang.split('-');
      if (splitLang.length > 1) {
        const languageCode = splitLang[0].toLowerCase();
        if (!userLangs.includes(languageCode)) {
          userLangs.push(languageCode);
        }
      }
    });
    return userLangs;
  }

  get availableAppLanguages() {
    return this.appLanguageCodes;
  }

  get availableCoreLanguages() {
    return this.coreLanguageCodes;
  }

  loadTranslationMaps(translations?: Record<string, TranslationMap>) {
    const appTranslations = translations ?? {};
    const coreLangKeys = Object.keys(coreLanguages);
    const langKeys = Object.keys(appTranslations);
    Vue.set(this, 'appLanguageCodes', langKeys);
    Vue.set(this, 'coreLanguageCodes', coreLangKeys);
    const allKeys = [...coreLangKeys, ...langKeys];
    allKeys.forEach((langCode) => {
      const coreTranslations = coreLanguages[langCode] ?? {};
      Vue.set(this.translations, langCode, { ...appTranslations[langCode], ...coreTranslations });
    });
    Vue.set(this.current, 'map', { ...this.translations[this.current.languageCode] });
  }

  set(languageCode: string) {
    Vue.$localStorage.set(this.storageKey, languageCode);
    Vue.set(this.current, 'languageCode', languageCode);
    Vue.set(this.current, 'map', { ...this.translations[languageCode] });
  }

  get() {
    return this.current.languageCode;
  }

  private getTranslatedValue(map: Record<string, any>, key: string): string | undefined {
    if (map[key]) {
      return map[key];
    }
    return _.deepGet<string | undefined>(this.current.map, key) as any;
  }

  translateEnum(key: string, enumValue: number | string, defaultString?: string): string {
    const translation = this.getSpecialTranslations(key);
    if (translation) {
      return translation;
    }
    const translationLookup = this.getTranslatedValue(this.current.map, key) as any;
    // Translation found
    if (translationLookup !== undefined) {
      const enumString = translationLookup[enumValue];
      if (enumString !== undefined) {
        return enumString;
      }
    }
    // No translation found
    if (defaultString === undefined) {
      defaultString = `[NO ENUM VALUE ${enumValue} FOR ${key}]`;
    } else {
      const defaultTranslation = this.getTranslatedValue(this.current.map, defaultString);
      if (defaultTranslation !== undefined) {
        defaultString = defaultTranslation;
      }
    }
    return defaultString;
  }

  translateAlternative<TAlt>(key: string | undefined | null, alternative: TAlt, ...args: any): string | TAlt {
    const parsedAlternative = typeof alternative === 'string' ? this.resolveTokens(alternative, ...args) : alternative;
    if (_.isEmpty(key)) {
      return parsedAlternative;
    }
    const translatedString = this.getTranslatedText(key!, ...args);
    return translatedString ?? parsedAlternative;
  }

  translate(key: string, ...args: any): string {
    const translation = this.getTranslatedText(key, ...args);
    return translation ?? `[${key}]`;
  }

  private tokenToString(token: any): string {
    let mappedToken = token;
    if (Array.isArray(token)) {
      mappedToken = mappedToken.join(', ');
    }
    if (mappedToken === null) {
      mappedToken = 'null';
    }
    if (mappedToken === undefined) {
      mappedToken = 'undefined';
    }
    return mappedToken;
  }

  private getSpecialTranslations(key: string | number) {
    if (key === undefined) {
      return '[UNDEFINED]';
    }
    if (key === null) {
      return '[NULL]';
    }
    if (key === '') {
      return '[EMPTY STRING]';
    }
    if (Array.isArray(key)) {
      return '[ARRAY]';
    }
    if (_.isObject(key)) {
      return '[OBJECT]';
    }
    return undefined;
  }

  private getTranslatedText(key: string, ...args: any): string | undefined {
    let translation = this.getSpecialTranslations(key);
    if (translation) {
      return translation;
    }
    translation = this.getTranslatedValue(this.current.map, key);
    if (translation !== undefined) {
      translation = this.resolveTokens(translation, ...args);
    }
    return translation;
  }

  private resolveTokens(translation: string, ...args: any[]) {
    const [firstToken] = args;
    if (args?.length === 1 && firstToken && (_.isObject(firstToken) || Array.isArray(firstToken))) {
      if (_.isObject(firstToken)) {
        Object.keys(firstToken).forEach((tokenKey) => {
          const stringToken = this.tokenToString(firstToken[tokenKey]);
          translation = translation?.replace(new RegExp(`\\{${tokenKey}\\}`, 'g'), stringToken);
        });
      } else if (Array.isArray(firstToken)) {
        (firstToken as any[]).forEach((element, iElement) => {
          const stringToken = this.tokenToString(element);
          translation = translation?.replace(new RegExp(`\\{${iElement}\\}`, 'g'), stringToken);
        });
      }
    } else {
      args.forEach((token: any, i: number) => {
        const stringToken = this.tokenToString(token);
        translation = translation?.replace(RegExp(`\\{${i}\\}`, 'g'), stringToken.toString());
      });
    }
    return translation;
  }
}
